/* eslint-disable @typescript-eslint/no-explicit-any */

import { ScreenWithFields } from '@/modules/templates/components/generate/dynamic-smart-flow/DynamicFormContext';
import { TemplateField } from '@/modules/templates/types/templates.types';
import { Briefcase, Building2, ClipboardCheck, FileText, PenTool, Scale, User } from 'lucide-react';
import { evaluateConditions } from '../conditions';

// Screen icons mapping based on common screen titles
export const SCREEN_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  company: Building2,
  employer: Building2,
  employee: User,
  work: Briefcase,
  job: Briefcase,
  compensation: Scale,
  salary: Scale,
  legal: Scale,
  signing: PenTool,
  signature: PenTool,
  review: ClipboardCheck,
  confirm: ClipboardCheck,
};

export function getScreenIcon(title: string): React.ComponentType<{ className?: string }> {
  const lowerTitle = title.toLowerCase();
  for (const [key, icon] of Object.entries(SCREEN_ICONS)) {
    if (lowerTitle.includes(key)) return icon;
  }
  return FileText;
}

/**
 * Extract variable names from a prompt template
 * Supports both simple {{varName}} and nested {{object.property}} syntax
 */
export function extractPromptVariables(prompt: string): string[] {
  const regex = /\{\{([^}]+)\}\}/g;
  const variables: string[] = [];
  let match;

  while ((match = regex.exec(prompt)) !== null) {
    const varName = match[1].trim();
    // For nested paths like "company.name", take the root variable
    const rootVar = varName.split('.')[0];
    if (!variables.includes(rootVar)) {
      variables.push(rootVar);
    }
  }

  return variables;
}

/**
 * Maps a variable name to its source screen and field definition.
 * Returns null if the variable doesn't correspond to any form field
 * (it might be from enrichment context instead).
 */
export function mapVariableToScreenAndField(
  variableName: string,
  screens: ScreenWithFields[]
): { screenIndex: number; field: TemplateField } | null {
  for (let screenIndex = 0; screenIndex < screens.length; screenIndex++) {
    const screen = screens[screenIndex];
    for (const field of screen.fields) {
      if (field.name === variableName) {
        return { screenIndex, field };
      }
    }
  }
  return null;
}

/**
 * Finds the screen index where a variable from enrichment context is generated.
 * Enrichment context values come from aiPrompt outputs on previous screens.
 * Returns the screen index if found, or -1 if not found.
 */
export function findEnrichmentSourceScreen(
  variableName: string,
  screens: ScreenWithFields[]
): number {
  for (let screenIndex = 0; screenIndex < screens.length; screenIndex++) {
    const screen = screens[screenIndex];
    const aiOutputSchema = (screen as any).aiOutputSchema;

    if (aiOutputSchema) {
      try {
        const schema =
          typeof aiOutputSchema === 'string' ? JSON.parse(aiOutputSchema) : aiOutputSchema;

        // Check if the variable is defined in this screen's output schema
        if (schema.properties && variableName in schema.properties) {
          return screenIndex;
        }
      } catch {
        // Skip invalid JSON schemas
      }
    }
  }
  return -1;
}

/**
 * Check if a value is considered "filled" (non-empty)
 */
export function isValueFilled(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return true;
}

/**
 * Check if a variable is resolved considering:
 * 1. Has a value in formData or enrichmentContext
 * 2. The field is not visible (conditions evaluate to false)
 * 3. User has passed the screen containing the field
 * 4. Variable comes from enrichmentContext (AI output from previous screen)
 */
export function isVariableResolved(
  variableName: string,
  formData: Record<string, unknown>,
  enrichmentContext: Record<string, unknown>,
  screens: ScreenWithFields[],
  currentStep: number
): boolean {
  // Check 1: Does the variable have a value?
  const allContext = { ...formData, ...enrichmentContext };
  if (isValueFilled(allContext[variableName])) {
    return true;
  }

  // Check 2: Is this a form field variable?
  const fieldMapping = mapVariableToScreenAndField(variableName, screens);

  if (fieldMapping) {
    const { screenIndex, field } = fieldMapping;

    // Check 2a: Is the field not visible due to conditions?
    const conditions = (field as any).conditions;
    if (conditions && !evaluateConditions(conditions, formData)) {
      // Field is hidden, user won't fill it - consider resolved
      return true;
    }

    // Check 2b: Has the user passed this screen?
    // currentStep is 0-indexed, so if currentStep > screenIndex, user has moved past
    if (currentStep > screenIndex) {
      // User has passed this screen - the field was either:
      // - Filled (handled in Check 1)
      // - Optional and skipped
      // - Hidden due to screen-level conditions
      return true;
    }

    // Field is visible and user hasn't passed it yet - not resolved
    return false;
  }

  // Check 3: Is this an enrichment context variable?
  const enrichmentSourceScreen = findEnrichmentSourceScreen(variableName, screens);

  if (enrichmentSourceScreen >= 0) {
    // Variable comes from AI enrichment on a previous screen
    // It's resolved if user has passed the screen that generates it
    if (currentStep > enrichmentSourceScreen) {
      // User completed the enrichment screen - even if no value,
      // the enrichment either ran or won't run
      return true;
    }
    // User hasn't passed the enrichment screen yet
    return false;
  }

  // Unknown variable source - consider it resolved to avoid blocking
  // (might be a typo in the prompt or a variable that will be added later)
  console.warn(`Unknown variable source for: ${variableName}`);
  return true;
}

/**
 * Check if all required variables for a prompt are resolved.
 * A variable is resolved if:
 * 1. It has a non-empty value in formData or enrichmentContext
 * 2. The field that provides it is not visible (conditions evaluate to false)
 * 3. The user has passed the screen containing the field
 * 4. For enrichment variables, the source screen has been completed
 *
 * This is the sophisticated calculation that considers conditional visibility
 * and screen navigation, not just whether values exist.
 */
export function arePromptVariablesResolved(
  prompt: string,
  formData: Record<string, unknown>,
  enrichmentContext: Record<string, unknown>,
  screens: ScreenWithFields[],
  currentStep: number
): boolean {
  const requiredVars = extractPromptVariables(prompt);
  if (requiredVars.length === 0) return true; // No variables needed, can fetch immediately

  return requiredVars.every((varName) =>
    isVariableResolved(varName, formData, enrichmentContext, screens, currentStep)
  );
}

/**
 * Check if all required variables for a prompt are available in the form data or enrichment context
 * @deprecated Use arePromptVariablesResolved() for sophisticated checking with visibility/navigation
 */
export function arePromptVariablesAvailable(
  prompt: string,
  formData: Record<string, unknown>,
  enrichmentContext: Record<string, unknown>
): boolean {
  const requiredVars = extractPromptVariables(prompt);
  if (requiredVars.length === 0) return true; // No variables needed, can fetch immediately

  const allContext = { ...formData, ...enrichmentContext };

  return requiredVars.every((varName) => {
    const value = allContext[varName];
    // Check if value exists and is not empty
    if (value === undefined || value === null || value === '') return false;
    // For strings, check if not just whitespace
    if (typeof value === 'string' && value.trim() === '') return false;
    return true;
  });
}

export function cloneFormData<T extends Record<string, unknown>>(data: T): T {
  try {
    return structuredClone(data);
  } catch (error) {
    console.error('structuredClone failed', error);
    // Fallback when structuredClone isn't available (should be rare in modern browsers)
    return JSON.parse(JSON.stringify(data));
  }
}

/**
 * Get nested value from object using dot notation (for enrichment context lookups)
 */
export function getNestedValue(obj: Record<string, unknown>, key: string): unknown {
  return key.split('.').reduce((acc: unknown, part: string) => {
    if (acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

/**
 * Get fields with available suggestions for a standard screen
 * Returns fields that have aiSuggestionEnabled and a value available in enrichmentContext
 */
export function getFieldsWithSuggestions(
  fields: Array<{
    name: string;
    aiSuggestionEnabled?: boolean;
    aiSuggestionKey?: string | null;
  }>,
  enrichmentContext: Record<string, unknown>
): Array<{ name: string; suggestedValue: unknown }> {
  const fieldsWithSuggestions: Array<{
    name: string;
    suggestedValue: unknown;
  }> = [];

  for (const field of fields) {
    if (field.aiSuggestionEnabled && field.aiSuggestionKey) {
      const suggestedValue = getNestedValue(enrichmentContext, field.aiSuggestionKey);
      if (suggestedValue !== undefined && suggestedValue !== null && suggestedValue !== '') {
        fieldsWithSuggestions.push({
          name: field.name,
          suggestedValue,
        });
      }
    }
  }

  return fieldsWithSuggestions;
}
